<template>
  <div class="capacity-apply-page">
    <div class="page-header">
      <div class="header-left">
        <span class="title">配送机构入驻</span>
      </div>
      <div class="header-right">
        <el-button link type="primary" @click="$router.push('/store/b/apply')">返回入驻目录</el-button>
        <el-button link type="primary" @click="$router.push('/store/login')">已有账号？去登录</el-button>
      </div>
    </div>
    <div class="page-body p-16px">
      <el-alert
        class="mb-16px"
        type="info"
        :closable="false"
        title="填写完整资料并设置登录账号。提交后由运营审核；通过后使用本账号登录 B 端工作台，进入运力经营台。资质附件本期为台账落库，不做三方 OCR。"
      />

      <el-card v-if="submittedOrgId && submitStatus === 'pending'" shadow="never">
        <el-result
          icon="success"
          title="申请已提交"
          :sub-title="`机构申请号 ${submittedOrgId}，请等待平台审核。审核通过后用填写的登录账号登录。`"
        >
          <template #extra>
            <el-button type="primary" @click="$router.push('/store/login')">去登录</el-button>
            <el-button @click="resetForm">继续填写 / 重新提交</el-button>
          </template>
        </el-result>
      </el-card>

      <el-card v-else shadow="never" header="提交入驻申请">
        <el-form ref="formRef" :model="form" :rules="rules" label-width="120px" class="max-w-560px">
          <div class="section-title">机构信息</div>
          <el-form-item label="机构名称" prop="name">
            <el-input v-model="form.name" maxlength="64" placeholder="对外展示名称" clearable />
          </el-form-item>
          <el-form-item label="服务城市" prop="city">
            <el-select v-model="form.city" class="!w-full" placeholder="选择城市" clearable>
              <el-option label="杭州" value="杭州" />
              <el-option label="上海" value="上海" />
              <el-option label="深圳" value="深圳" />
            </el-select>
          </el-form-item>
          <el-form-item label="信用代码" prop="licenseNo">
            <el-input v-model="form.licenseNo" maxlength="32" placeholder="统一社会信用代码" clearable />
          </el-form-item>
          <el-form-item label="营业执照" prop="licenseUrl">
            <UploadImg v-model="form.licenseUrl" directory="capacity/org/license" />
          </el-form-item>
          <el-form-item label="法人姓名" prop="legalPerson">
            <el-input v-model="form.legalPerson" maxlength="32" placeholder="法人姓名" clearable />
          </el-form-item>
          <el-form-item label="身份证正面" prop="idCardFront">
            <UploadImg v-model="form.idCardFront" directory="capacity/org/idcard" />
          </el-form-item>
          <el-form-item label="身份证反面" prop="idCardBack">
            <UploadImg v-model="form.idCardBack" directory="capacity/org/idcard" />
          </el-form-item>

          <div class="section-title">联系信息</div>
          <el-form-item label="联系手机" prop="contactPhone">
            <el-input v-model="form.contactPhone" maxlength="11" placeholder="11 位手机号" clearable />
          </el-form-item>
          <el-form-item label="联系邮箱" prop="contactEmail">
            <el-input v-model="form.contactEmail" maxlength="64" placeholder="选填" clearable />
          </el-form-item>

          <div class="section-title">管理员账号</div>
          <el-form-item label="登录账号" prop="loginUsername">
            <el-input
              v-model="form.loginUsername"
              maxlength="32"
              placeholder="审通后用于 B 端登录，可用手机号"
              clearable
            />
          </el-form-item>
          <el-form-item label="登录密码" prop="loginPassword">
            <el-input
              v-model="form.loginPassword"
              type="password"
              show-password
              maxlength="20"
              placeholder="6-20 位"
            />
          </el-form-item>
          <el-form-item label="确认密码" prop="confirmPassword">
            <el-input
              v-model="form.confirmPassword"
              type="password"
              show-password
              maxlength="20"
              placeholder="再次输入密码"
            />
          </el-form-item>

          <el-form-item>
            <el-button type="primary" :loading="submitting" @click="onSubmit">提交申请</el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>
  </div>
</template>

<script lang="ts" setup>
import * as OrgConsoleApi from '@/api/capacity/org-console'
import { getTenantIdByName } from '@/api/login'
import { UploadImg } from '@/components/UploadFile'
import { getTenantId, setTenantId } from '@/utils/auth'
import type { FormInstance, FormRules } from 'element-plus'
import { ElMessage } from 'element-plus'

defineOptions({ name: 'MerchantCapacityApply' })

const formRef = ref<FormInstance>()
const submitting = ref(false)
const submittedOrgId = ref('')
const submitStatus = ref('')

const form = reactive({
  name: '',
  city: '',
  licenseNo: '',
  licenseUrl: '',
  legalPerson: '',
  idCardFront: '',
  idCardBack: '',
  contactPhone: '',
  contactEmail: '',
  loginUsername: '',
  loginPassword: '',
  confirmPassword: ''
})

const validateConfirm = (_rule: any, value: string, callback: (e?: Error) => void) => {
  if (value !== form.loginPassword) {
    callback(new Error('两次输入的密码不一致'))
    return
  }
  callback()
}

const rules: FormRules = {
  name: [{ required: true, message: '请填写机构名称', trigger: 'blur' }],
  city: [{ required: true, message: '请选择城市', trigger: 'change' }],
  licenseNo: [{ required: true, message: '请填写信用代码', trigger: 'blur' }],
  licenseUrl: [{ required: true, message: '请上传营业执照', trigger: 'change' }],
  legalPerson: [{ required: true, message: '请填写法人姓名', trigger: 'blur' }],
  idCardFront: [{ required: true, message: '请上传身份证正面', trigger: 'change' }],
  idCardBack: [{ required: true, message: '请上传身份证反面', trigger: 'change' }],
  contactPhone: [
    { required: true, message: '请填写联系人手机', trigger: 'blur' },
    { pattern: /^1\d{10}$/, message: '请输入 11 位手机号', trigger: 'blur' }
  ],
  loginUsername: [{ required: true, message: '请填写登录账号', trigger: 'blur' }],
  loginPassword: [
    { required: true, message: '请填写登录密码', trigger: 'blur' },
    { min: 6, max: 20, message: '密码 6-20 位', trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, message: '请确认密码', trigger: 'blur' },
    { validator: validateConfirm, trigger: 'blur' }
  ]
}

async function ensureTenant() {
  if (getTenantId()) return
  const name = import.meta.env.VITE_APP_DEFAULT_LOGIN_TENANT || '紫金山'
  try {
    const id = await getTenantIdByName(name)
    if (id != null) setTenantId(Number(id))
  } catch {
    // 仍尝试提交
  }
}

const onSubmit = async () => {
  await formRef.value?.validate()
  submitting.value = true
  try {
    await ensureTenant()
    if (!form.loginUsername.trim()) {
      form.loginUsername = form.contactPhone
    }
    const r = await OrgConsoleApi.orgApplyOpen({
      name: form.name.trim(),
      city: form.city,
      licenseNo: form.licenseNo.trim(),
      licenseUrl: form.licenseUrl,
      legalPerson: form.legalPerson.trim(),
      idCardFront: form.idCardFront,
      idCardBack: form.idCardBack,
      contactPhone: form.contactPhone.trim(),
      contactEmail: form.contactEmail?.trim() || undefined,
      loginUsername: form.loginUsername.trim(),
      loginPassword: form.loginPassword,
      confirmPassword: form.confirmPassword
    })
    if (!r.success) {
      ElMessage.error(r.message || '提交失败')
      return
    }
    submittedOrgId.value = r.orgId || ''
    submitStatus.value = 'pending'
    ElMessage.success('已提交，等待平台审核')
  } finally {
    submitting.value = false
  }
}

function resetForm() {
  submittedOrgId.value = ''
  submitStatus.value = ''
}

watch(
  () => form.contactPhone,
  (v) => {
    if (!form.loginUsername) {
      form.loginUsername = v
    }
  }
)

onMounted(ensureTenant)
</script>

<style scoped lang="scss">
.capacity-apply-page {
  /* 全局 body overflow:hidden，本页自建滚动容器（对齐上门机构入驻） */
  height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  background: #f5f7fa;
}
.page-header {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
  .title {
    font-size: 18px;
    font-weight: 600;
  }
  .header-right {
    display: flex;
    gap: 8px;
  }
}
.page-body {
  max-width: 720px;
  margin: 0 auto;
  padding-bottom: 48px;
}
.section-title {
  font-weight: 600;
  margin: 8px 0 16px;
  color: #303133;
}
</style>
